import type { ConsentChoice } from '@lyrmo/shared';
import { useMemo, useState, type ReactNode } from 'react';
import type { ApiClient } from '../../api/client';
import { readConsent, writeConsent } from '../../lib/consent';
import { ConsentContext, type Consent } from '../../lib/consent-context';
import { forgetUse, localStore } from '../../lib/tracking';

interface ConsentProviderProps {
  readonly client: Pick<ApiClient, 'recordConsent'>;
  readonly children: ReactNode;
}

/**
 * Keeps the visitor's choice about Lyrmo's own insights: asked for until it's made, kept for six
 * months, and changed whenever they like. Only allowing them lets the site note what's done.
 * Each choice made is counted, as no one's.
 */
export function ConsentProvider({ client, children }: ConsentProviderProps) {
  const [choice, setChoice] = useState<ConsentChoice | null>(() => readConsent());
  const [reopened, setReopened] = useState(false);

  const consent = useMemo<Consent>(
    () => ({
      choice,
      asking: choice === null || reopened,
      choose: (next) => {
        writeConsent(next);
        // A choice that can't be counted is still made.
        client.recordConsent(next).catch(() => undefined);
        // Declining forgets when the site was last used, which only insights need.
        if (next === 'necessary') forgetUse(localStore());
        setChoice(next);
        setReopened(false);
      },
      ask: () => {
        setReopened(true);
      },
    }),
    [choice, reopened, client],
  );

  return <ConsentContext value={consent}>{children}</ConsentContext>;
}
